import { getSelectedKeyboardLayoutConfig } from "../../../lib/utils";
import {
  escapeHtml,
  mountHQPanel,
  numberOr,
  removeHQPanel,
  renderTextWithHighlights,
  resolveKeyForLayout,
} from "./hqShared.js";

const ORANGE = "#DE5700";
const TEXT_COLOR = "#0A0A0A";
const HINT_COLOR = "#828282";
const CARD_BG = "#FFFFFF";
const LANE_NUMBERS = ["1", "2", "3", "5", "6", "7"];

function autoFitTextToContainer(textEl, containerEl, minFontSizePx) {
  if (!textEl || !containerEl) return;

  const computed = window.getComputedStyle(textEl);
  let fontSize = Number.parseFloat(computed.fontSize);
  if (!Number.isFinite(fontSize)) return;

  const minSize = Number.isFinite(minFontSizePx) ? minFontSizePx : 12;

  while (
    fontSize > minSize
    && (textEl.scrollWidth > containerEl.clientWidth || textEl.scrollHeight > containerEl.clientHeight)
  ) {
    fontSize -= 1;
    textEl.style.fontSize = `${fontSize}px`;
  }
}

function fitHQLaneChoiceMessageText(wrapper, config = {}) {
  const mainTextEl = wrapper.querySelector('[data-hq-lane-choice-main-text="true"]');
  const mainTextContainerEl = wrapper.querySelector('[data-hq-lane-choice-main-container="true"]');

  const minMainFontSizePx = Number.isFinite(config.minMainFontSizePx)
    ? config.minMainFontSizePx
    : 8;

  autoFitTextToContainer(mainTextEl, mainTextContainerEl, minMainFontSizePx);
}

export function buildHQLaneChoiceMessageHTML(jsPsych, config = {}) {
  const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);

  const {
    portrait = "hq-impressed",
    text = "Choose a lane.",
    highlights = [],
    laneKeyCodes = ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
    continueKey = "Space",
    showContinueHint = true,
    widthPx = 790,
    maxWidth = "60%",
    heightPx = 100,
    minHeightPx = 70,
    portraitSizePx = 70,
    mainFontSizePx = 22,
    keyFontSizePx = 30,
    continueFontSizePx = 16,
  } = config;

  const portraitSrc = escapeHtml(`assets/images/instructions/custom/${portrait}.svg`);
  const cardHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : "auto";
  const cardMinHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : `${minHeightPx}px`;
  const cardMaxWidthCss = maxWidth === "none" ? `${widthPx}px` : `min(${widthPx}px, ${maxWidth})`;
  const mainFontSizeValuePx = numberOr(mainFontSizePx, 22);
  const keyFontSizeValuePx = numberOr(keyFontSizePx, 30);
  const continueFontSizeValuePx = numberOr(continueFontSizePx, 16);
  const portraitRenderSizePx = numberOr(portraitSizePx, 70) + 25;
  const keyLabelSizeCss = `clamp(${Math.max(4, keyFontSizeValuePx - 6)}px, 2.2vw, ${keyFontSizeValuePx}px)`;
  const mainFontSizeCss = `clamp(${Math.max(12, mainFontSizeValuePx - 10)}px, 1.7vw, ${mainFontSizeValuePx}px)`;
  const continueFontSizeCss = `clamp(12px, 1.2vw, ${continueFontSizeValuePx}px)`;

  const renderedText = renderTextWithHighlights(text, highlights, layoutConfig, {
    highlightColor: ORANGE,
  });
  const resolvedContinueKey = resolveKeyForLayout(continueKey, layoutConfig);
  const laneLabels = laneKeyCodes.map((keyCode) => escapeHtml(resolveKeyForLayout(keyCode, layoutConfig)));
  const keyPills = laneLabels
    .map((label, index) => {
      const laneNumber = escapeHtml(LANE_NUMBERS[index] ?? "");
      return `
    <span class="gear-hq-key-pill">
      <span class="gear-hq-key-number">${laneNumber}</span>
      <span class="gear-hq-key-label">${label}</span>
    </span>`;
    })
    .join("");

  return `
    <div class="gear-hq-card" style="
      --hq-card-max-width:${cardMaxWidthCss};
      --hq-card-height:${cardHeightCss};
      --hq-card-min-height:${cardMinHeightCss};
      --hq-portrait-size:${portraitRenderSizePx}px;
      --hq-lane-bottom-pad:${showContinueHint ? 28 : 8}px;
      --hq-lane-main-font-size:${mainFontSizeCss};
      --hq-continue-font-size:${continueFontSizeCss};
      --hq-key-label-size:${keyLabelSizeCss};
      background:${CARD_BG};
    ">
      <div class="gear-hq-portrait-shell">
        <img
          src="${portraitSrc}"
          alt="HQ portrait"
          class="gear-hq-portrait-img"
        />
      </div>

      <div class="gear-hq-lane-body">
        <div class="gear-hq-lane-main gear-hq-main-text" style="line-height:1.2;margin:0;color:${TEXT_COLOR};" data-hq-lane-choice-main-container="true">
          <div style="min-width:0;" data-hq-lane-choice-main-text="true">${renderedText}</div>
        </div>

        <div class="gear-hq-lane-keys">${keyPills}</div>
      </div>

      ${showContinueHint ? `
      <div class="gear-hq-continue-hint" style="color:${HINT_COLOR};">[press <strong style="font-weight:600;color:${HINT_COLOR};">${escapeHtml(resolvedContinueKey)}</strong> to continue]</div>
      ` : ""}
    </div>
  `;
}

export function injectHQLaneChoiceMessage(jsPsych, config = {}) {
  const verticalOffsetPx = Number.isFinite(config?.verticalOffsetPx)
    ? config.verticalOffsetPx
    : -6;

  const wrapper = mountHQPanel({
    panelId: "hq-lane-choice-panel",
    html: buildHQLaneChoiceMessageHTML(jsPsych, config),
    verticalOffsetPx,
  });
  if (!wrapper) return null;

  window.requestAnimationFrame(() => {
    fitHQLaneChoiceMessageText(wrapper, {
      minMainFontSizePx: config?.minMainFontSizePx,
    });
  });

  return wrapper;
}

export function removeHQLaneChoiceMessage() {
  removeHQPanel("hq-lane-choice-panel");
}
